import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import AppInfo from '../components/AppInfo';
import { Sparkles, Globe, Flag, DollarSign, Database, Lock } from 'lucide-react';
import { toast } from 'sonner';
import AnalyzeTab from './dashboard/AnalyzeTab';
import ErpTab from './dashboard/ErpTab';
import SourcingTab from './dashboard/SourcingTab';
import OriginTab from './dashboard/OriginTab';
import FinancialTab from './dashboard/FinancialTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = ({ user, setUser }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState(
    searchParams.get('tab') === 'sourcing' ? 'sourcing' :
    searchParams.get('tab') === 'origin' ? 'origin' :
    searchParams.get('tab') === 'financial' ? 'financial' :
    searchParams.get('tab') === 'erp' ? 'erp' : 'analyze'
  );

  const [appAccess, setAppAccess] = useState(null);

  // Sync tab with URL query param
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'sourcing') setActiveTab('sourcing');
    else if (tab === 'origin') setActiveTab('origin');
    else if (tab === 'financial') setActiveTab('financial');
    else if (tab === 'erp') setActiveTab('erp');
    else setActiveTab('analyze');
  }, [searchParams]);

  // Fetch app access on mount
  useEffect(() => {
    const fetchAccess = async () => {
      try {
        const token = localStorage.getItem('decoded_token');
        if (!token) return;
        const resp = await fetch(`${API_URL}/api/me/access`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (resp.ok) {
          const data = await resp.json();
          setAppAccess(data.access);
        }
      } catch (e) { /* silently allow all tabs while loading */ }
    };
    fetchAccess();
  }, []);

  const canAccessApp = (tabName) => {
    if (!appAccess) return true;
    const appMapping = {
      'analyze': 'product_analyzer',
      'financial': 'financial_analyzer',
      'sourcing': 'smart_sourcing',
      'origin': 'origin_preference',
      'erp': 'erp_analyzer'
    };
    const appId = appMapping[tabName];
    if (!appId) return true;
    return appAccess[appId]?.allowed !== false;
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'analyze') setSearchParams({});
    else setSearchParams({ tab });
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      <Sidebar user={user} setUser={setUser} activePage="analyzer" />

      <main className="flex-1 p-6 lg:p-8 lg:pt-24">
        <div className="max-w-6xl mx-auto">
          {/* Header with Tabs */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="dashboard-tabs">
              {[
                { id: 'analyze', label: 'Product Analysis', icon: Sparkles, activeColors: 'from-[#2563EB]/10 to-[#06B6D4]/10 text-[#2563EB]' },
                { id: 'sourcing', label: 'Smart Sourcing', icon: Globe, activeColors: 'from-[#10B981]/10 to-[#06B6D4]/10 text-[#10B981]' },
                { id: 'origin', label: 'Origin & Preference', icon: Flag, activeColors: 'from-[#8B5CF6]/10 to-[#EC4899]/10 text-[#8B5CF6]' },
                { id: 'financial', label: 'Financial Impact', icon: DollarSign, activeColors: 'from-[#059669]/10 to-[#10B981]/10 text-[#059669]' },
                { id: 'erp', label: 'ERP Smart Import', icon: Database, activeColors: 'from-[#8B5CF6]/10 to-[#EC4899]/10 text-[#8B5CF6]', premium: true }
              ].map((tab) => {
                const hasAccess = canAccessApp(tab.id);
                const TabIcon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => hasAccess ? handleTabChange(tab.id) : toast.error(`Upgrade your plan to access ${tab.label}`)}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                      !hasAccess
                        ? 'text-slate-300 cursor-not-allowed'
                        : activeTab === tab.id
                        ? `bg-gradient-to-r ${tab.activeColors}`
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    {hasAccess ? <TabIcon className="w-4 h-4" /> : <Lock className="w-3.5 h-3.5" />}
                    {tab.label}
                    {tab.premium && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] text-white">Pro</span>}
                  </button>
                );
              })}
            </div>
            {activeTab !== 'financial' && activeTab !== 'erp' && (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <h1 className="font-outfit text-3xl font-bold text-slate-900" data-testid="dashboard-title">
                    {activeTab === 'analyze' ? 'Analyze your products' :
                     activeTab === 'sourcing' ? 'Smart Sourcing Optimizer' :
                     'Origin & Preference Engine'}
                  </h1>
                  <AppInfo appId={activeTab === 'analyze' ? 'analyzer' : activeTab === 'sourcing' ? 'sourcing' : 'origin'} />
                </div>
                <p className="text-slate-500 font-jakarta">
                  {activeTab === 'analyze'
                    ? 'Discover tariff optimization opportunities with AI'
                    : activeTab === 'sourcing'
                    ? 'Find the best countries to source your products from'
                    : 'Validate origin, check FTA eligibility, and model scenarios'}
                </p>
              </>
            )}
          </div>

          {/* Delegated tab renderers */}
          {activeTab === 'analyze' && <AnalyzeTab user={user} setUser={setUser} setActiveTab={setActiveTab} />}
          {activeTab === 'erp' && <ErpTab user={user} setUser={setUser} setActiveTab={setActiveTab} />}
          {activeTab === 'sourcing' && <SourcingTab user={user} setUser={setUser} setActiveTab={setActiveTab} />}
          {activeTab === 'origin' && <OriginTab user={user} setUser={setUser} setActiveTab={setActiveTab} />}
          {activeTab === 'financial' && <FinancialTab user={user} setUser={setUser} setActiveTab={setActiveTab} />}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
